/**
 * O contrato da organização, com a edição no lugar (#765).
 *
 * Os valores vieram do plano no dia da contratação e podem ser ajustados só
 * para este cliente. Editar abre os campos dentro da própria ficha, sem
 * diálogo: a pessoa vê o que está mudando ao lado do resto do contrato.
 *
 * ── "Personalizado" diz de onde o valor saiu ──────────────────────────────
 *
 * Quando o usuário adicional deste cliente sai do valor do plano, o chip diz
 * isso com o valor do plano ao lado. Sem ele, quem lê o contrato daqui a seis
 * meses não sabe se R$ 54,90 foi negociado ou erro de digitação.
 */

import { useState, type ReactNode } from 'react';
import { Botao, Campo, Cartao, Chip, Ficha } from '@/shared/ui';
import {
  comUmaCasa,
  diaMesAno,
  dinheiroComCentavos,
  emMicros,
  mascararReais,
} from '@/shared/lib/formato';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Vazio } from '@/shared/ui/estados/estados';
import type {
  Dinheiro,
  MudancaDoContrato,
  OrganizacaoDetalhada,
} from '../model/organizacao-detalhada';
import {
  ehPersonalizado,
  emReaisParaDigitar,
  lerEdicao,
  lerEdicaoDoArmazenamento,
  oQueMudou,
  oQueMudouNoArmazenamento,
} from '../lib/contrato';

type Erros = {
  readonly usuarios?: string;
  readonly adicional?: string;
  readonly armazenamento?: string;
  readonly gbAdicional?: string;
};

export function PainelDoContrato({
  organizacao,
  aoSalvar,
}: {
  organizacao: OrganizacaoDetalhada;
  aoSalvar: (mudanca: MudancaDoContrato) => Promise<void>;
}): ReactNode {
  const { contrato, plano, uso } = organizacao;
  const [editando, definirEditando] = useState(false);
  const [usuarios, definirUsuarios] = useState('');
  const [adicional, definirAdicional] = useState('');
  const [armazenamento, definirArmazenamento] = useState('');
  const [gbAdicional, definirGbAdicional] = useState('');
  /**
   * O que estava nos campos quando a edição abriu: o pedido leva só o que mudou
   * disso, e a recusa do valor vazio olha o valor de então, e não o de uma
   * releitura que trouxe a mudança de outra pessoa no meio.
   */
  const [preenchido, definirPreenchido] = useState<{
    usuarios: string;
    adicional: string;
    adicionalAoAbrir: Dinheiro | null;
    armazenamento: string;
    gbAdicional: string;
    gbAdicionalAoAbrir: Dinheiro | null;
  }>({
    usuarios: '',
    adicional: '',
    adicionalAoAbrir: null,
    armazenamento: '',
    gbAdicional: '',
    gbAdicionalAoAbrir: null,
  });
  const [erros, definirErros] = useState<Erros>({});
  const [falha, definirFalha] = useState<string | null>(null);
  const [salvando, definirSalvando] = useState(false);

  if (!contrato) {
    return (
      <Vazio
        titulo="Sem contrato"
        porque="Esta organização ainda não tem contrato. Ele nasce com a organização, a partir de um plano do catálogo."
      />
    );
  }

  const moeda = contrato.usuarioAdicional?.moeda ?? plano?.usuarioAdicional?.moeda ?? 'BRL';
  const moedaDoGb = contrato.gbAdicional?.moeda ?? plano?.gbAdicional?.moeda ?? 'BRL';
  const adicionalNaTela: Dinheiro | null = editando
    ? ((micros) => (micros === null ? null : { quantia_micros: micros, moeda }))(
        emMicros(adicional),
      )
    : contrato.usuarioAdicional;
  const personalizado = ehPersonalizado(adicionalNaTela, plano?.usuarioAdicional ?? null);

  const abrir = () => {
    const campos = {
      usuarios: String(contrato.usuariosContratados),
      adicional: emReaisParaDigitar(contrato.usuarioAdicional),
      adicionalAoAbrir: contrato.usuarioAdicional,
      armazenamento: String(contrato.armazenamentoContratadoGb),
      gbAdicional: emReaisParaDigitar(contrato.gbAdicional),
      gbAdicionalAoAbrir: contrato.gbAdicional,
    };
    definirPreenchido(campos);
    definirUsuarios(campos.usuarios);
    definirAdicional(campos.adicional);
    definirArmazenamento(campos.armazenamento);
    definirGbAdicional(campos.gbAdicional);
    definirErros({});
    definirFalha(null);
    definirEditando(true);
  };

  const salvar = async () => {
    const leitura = lerEdicao(usuarios, adicional, moeda, preenchido.adicionalAoAbrir);
    const doArmazenamento = lerEdicaoDoArmazenamento(
      armazenamento,
      gbAdicional,
      moedaDoGb,
      preenchido.gbAdicionalAoAbrir,
    );
    if (!leitura.ok || !doArmazenamento.ok) {
      definirErros({
        ...(leitura.ok ? {} : leitura.erros),
        ...(doArmazenamento.ok ? {} : doArmazenamento.erros),
      });
      return;
    }
    definirErros({});
    definirFalha(null);
    const mudanca = {
      ...oQueMudou(leitura.valores, preenchido),
      ...oQueMudouNoArmazenamento(doArmazenamento.valores, preenchido),
    };
    // Nada mudou: a edição fecha sem pedir nada ao servidor.
    if (Object.keys(mudanca).length === 0) {
      definirEditando(false);
      return;
    }
    definirSalvando(true);
    try {
      await aoSalvar(mudanca);
      definirEditando(false);
    } catch (erro) {
      definirFalha(recadoDeErro(erro, { generica: 'Não consegui salvar o contrato.' }));
    } finally {
      definirSalvando(false);
    }
  };

  const chip = personalizado ? (
    <Chip tom="etapa">
      Personalizado · no plano, {dinheiroComCentavos(plano?.usuarioAdicional ?? null)}
    </Chip>
  ) : null;

  const blocoDeGb = contrato.gbAdicional
    ? dinheiroComCentavos({
        quantia_micros: contrato.gbAdicional.quantia_micros * 10,
        moeda: contrato.gbAdicional.moeda,
      })
    : null;

  return (
    <Cartao variante="folgado" como="section" aria-labelledby="organizacao-contrato">
      <div className="organizacao__cab-do-painel">
        <h3 id="organizacao-contrato" className="organizacao__titulo-do-painel">
          Contrato
        </h3>
        {editando ? (
          <>
            <Botao
              variante="fantasma"
              miudo
              desabilitado={salvando}
              motivo="Salvando o contrato"
              onClick={() => {
                definirEditando(false);
                // A falha de um salvamento recusado não fica sobre os valores de antes (#1160).
                definirFalha(null);
              }}
            >
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              miudo
              desabilitado={salvando}
              motivo="Salvando o contrato"
              onClick={() => void salvar()}
            >
              {salvando ? 'Salvando…' : 'Salvar'}
            </Botao>
          </>
        ) : organizacao.exclusao ? null : (
          // Na excluída, a gestão acabou: o contrato não muda mais (#1160).
          <Botao miudo onClick={abrir}>
            Editar contrato
          </Botao>
        )}
      </div>
      <div className="organizacao__fichas">
        <Ficha
          larguraRotulo="168px"
          linhas={[
            { rotulo: 'Plano', valor: plano?.nome ?? 'Sem plano' },
            { rotulo: 'Início do contrato', valor: diaMesAno(contrato.inicio) },
            {
              rotulo: 'Usuários contratados',
              valor: editando ? (
                <span className="organizacao__campo">
                  <Campo
                    tamanho="miudo"
                    rotuloOculto="Usuários contratados"
                    inputMode="numeric"
                    value={usuarios}
                    erro={erros.usuarios}
                    onChange={(evento) => definirUsuarios(evento.target.value)}
                  />
                </span>
              ) : (
                <>
                  {contrato.usuariosContratados}
                  <span className="organizacao__apoio">{uso.usuariosEmUso} em uso</span>
                </>
              ),
            },
            {
              rotulo: 'Usuário adicional deste cliente',
              valor: editando ? (
                <span className="organizacao__valor-editavel">
                  <span className="organizacao__campo">
                    <Campo
                      tamanho="miudo"
                      rotuloOculto="Valor do usuário adicional deste cliente"
                      inputMode="decimal"
                      value={adicional}
                      erro={erros.adicional}
                      onChange={(evento) => definirAdicional(mascararReais(evento.target.value))}
                    />
                  </span>
                  {chip}
                </span>
              ) : (
                <span className="organizacao__valor-editavel">
                  {dinheiroComCentavos(contrato.usuarioAdicional)
                    ? `${dinheiroComCentavos(contrato.usuarioAdicional)} por mês`
                    : 'A definir'}
                  {chip}
                </span>
              ),
            },
          ]}
        />
        <Ficha
          larguraRotulo="168px"
          linhas={[
            {
              rotulo: 'Armazenamento contratado',
              valor: editando ? (
                <span className="organizacao__campo">
                  <Campo
                    tamanho="miudo"
                    rotuloOculto="Armazenamento contratado, em GB"
                    inputMode="numeric"
                    value={armazenamento}
                    erro={erros.armazenamento}
                    onChange={(evento) => definirArmazenamento(evento.target.value)}
                  />
                </span>
              ) : (
                <>
                  {comUmaCasa(contrato.armazenamentoContratadoGb)} GB
                  {uso.armazenamentoGb === null ? null : (
                    <span className="organizacao__apoio">
                      {comUmaCasa(uso.armazenamentoGb)} GB em uso
                    </span>
                  )}
                </>
              ),
            },
            {
              rotulo: 'GB adicional deste cliente',
              valor: editando ? (
                <span className="organizacao__campo">
                  <Campo
                    tamanho="miudo"
                    rotuloOculto="Valor do GB adicional deste cliente"
                    inputMode="decimal"
                    value={gbAdicional}
                    erro={erros.gbAdicional}
                    onChange={(evento) => definirGbAdicional(mascararReais(evento.target.value))}
                  />
                </span>
              ) : contrato.gbAdicional ? (
                <>
                  {dinheiroComCentavos(contrato.gbAdicional)} por GB
                  <span className="organizacao__apoio">Bloco de 10 GB: {blocoDeGb}</span>
                </>
              ) : (
                'A definir'
              ),
            },
            {
              rotulo: 'Contato financeiro',
              valor: contrato.contatoFinanceiro ? (
                <>
                  {contrato.contatoFinanceiro.nome}
                  <span className="organizacao__apoio">{contrato.contatoFinanceiro.email}</span>
                </>
              ) : (
                'Não informado'
              ),
            },
          ]}
        />
      </div>
      {falha ? (
        <p className="organizacao__falha" role="alert">
          {falha}
        </p>
      ) : null}
      {plano ? (
        <p className="organizacao__nota">
          Os valores vieram do plano {plano.nome} no dia da contratação. Mudar o plano depois não
          muda o que este cliente paga.
        </p>
      ) : null}
    </Cartao>
  );
}
